import React, { useState, useEffect } from 'react';

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { UserProfile, Promotion, CheckoutItem } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { useI18n } from 'src/i18n/i18nContext';
import { useLocalizedNavigate } from 'src/hooks/useLocalizedNavigate';
import { paymentAPI, userAPI } from 'src/services/api';
import { clearEcommerce, formatSessionBundleItem, pushToDataLayer } from 'src/utils/dataLayer';
import { getErrorMessage } from 'src/utils/apiErrorHandler';

const CheckoutForm: React.FC<{
  checkoutItem: CheckoutItem;
  giftCardBalance: number;
  finalAmount: number;
  user: any;
  userProfile: UserProfile | null;
  appliedGiftCard: { code: string; balance: number } | null;
  promotion: Promotion | null;
  initialSessionBundleIds: string[];
  initialSubIds: string[];
}> = ({
  checkoutItem,
  giftCardBalance,
  finalAmount,
  user,
  userProfile,
  appliedGiftCard,
  promotion,
  initialSessionBundleIds,
  initialSubIds,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();
  const navigate = useLocalizedNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isPollingOrder, setIsPollingOrder] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || '');
      setEmail(userProfile.email || '');
    } else if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user, userProfile]);

  const waitForOrderCompletion = async (
    type: 'subscription' | 'session_bundle' | 'package_pack'
  ) => {
    if (!user && !userProfile) return;

    if (type === 'package_pack') {
      return;
    }

    setIsPollingOrder(true);
    const maxAttempts = 10;
    const minDisplayTime = 2000;
    const pollStartTime = Date.now();
    let attempts = 0;

    const checkData = async () => {
      try {
        if (type === 'subscription') {
          const res = await userAPI.getSubscriptions();
          const resData = res.data as any;
          const list = Array.isArray(resData) ? resData : resData?.data || [];
          return list.some((item: any) => !initialSubIds.includes(item.id));
        } else {
          const res = await userAPI.getUserSessionBundles();
          const resData = res.data as any;
          const list = Array.isArray(resData) ? resData : resData?.data || [];
          return list.some((item: any) => !initialSessionBundleIds.includes(item.id));
        }
      } catch (e) {
        return false;
      }
    };

    while (attempts < maxAttempts) {
      const success = await checkData();
      if (success) break;
      await new Promise((r) => setTimeout(r, 2000));
      attempts++;
    }

    const elapsed = Date.now() - pollStartTime;
    if (elapsed < minDisplayTime) {
      await new Promise((r) => setTimeout(r, minDisplayTime - elapsed));
    }

    setIsPollingOrder(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttemptedSubmit(true);

    if (!stripe || !elements) {
      return;
    }

    if (name.trim() === '' || email.trim() === '') {
      return;
    }

    // GTM: Fire add_payment_info when user initiates payment (clicks Pay)
    if (checkoutItem) {
      clearEcommerce();
      const category =
        checkoutItem.type === 'subscription'
          ? 'subscription'
          : checkoutItem.type === 'package_pack'
            ? 'package_pack'
            : 'session_bundle';
      const itemType = checkoutItem.type === 'subscription' ? 'Subscription' : 'Session Bundle';
      const couponValue =
        appliedGiftCard?.code ||
        (promotion?.title
          ? typeof promotion.title === 'string'
            ? promotion.title
            : promotion.title.en || Object.values(promotion.title)[0]
          : undefined);

      pushToDataLayer('add_payment_info', {
        content: { category },
        ecommerce: {
          payment_type: 'pay_with_card',
          ...(couponValue && { coupon: couponValue }),
          items:
            checkoutItem.type === 'package_pack'
              ? [
                  {
                    item_id: checkoutItem.id,
                    item_name: checkoutItem.name,
                    item_category: 'Package Pack',
                    value: checkoutItem.price,
                  },
                ]
              : [
                  formatSessionBundleItem(
                    { id: checkoutItem.id, name: checkoutItem.name, price: checkoutItem.price },
                    itemType
                  ),
                ],
        },
        customer: {
          email: userProfile?.email || user?.email || '',
          phone: userProfile?.phoneNumber || user?.phone_number || '',
        },
      });
    }

    setIsProcessing(true);
    setError(null);

    try {
      const card = elements.getElement(CardNumberElement);
      if (!card) {
        throw new Error(t.checkout.cardElementError);
      }

      let paymentIntentResponse;
      if (checkoutItem.type === 'subscription') {
        paymentIntentResponse = await paymentAPI.createSubscriptionIntent({
          subscriptionId: checkoutItem.id,
          giftCardCode: appliedGiftCard?.code,
          promoCode: promotion?.code,
        });
      } else {
        paymentIntentResponse = await paymentAPI.createPaymentIntent({
          ...(checkoutItem.type === 'package_pack'
            ? { packagePackId: checkoutItem.id }
            : { sessionBundleId: checkoutItem.id }),
          giftCardCode: appliedGiftCard?.code,
          promoCode: promotion?.code,
        });
      }

      const responseData = paymentIntentResponse.data as any;
      const { clientSecret, intentType, subscriptionId, requiresAction } = responseData;

      // Handle subscription without client secret (new flow)
      if (intentType === 'subscription' && !clientSecret && requiresAction) {
        // Create payment method first
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            name: name,
            email: email,
          },
        });

        if (pmError) {
          setError(pmError.message || t.checkout.paymentError);
          setIsProcessing(false);
          return;
        }

        // Confirm subscription with the payment method
        const confirmResponse = await paymentAPI.confirmSubscription({
          subscriptionId: subscriptionId,
          paymentMethodId: paymentMethod?.id,
        });

        const confirmData = confirmResponse.data as any;
        if (confirmData.requiresAction && confirmData.clientSecret) {
          // 3D Secure confirmation required
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            confirmData.clientSecret
          );

          if (confirmError) {
            throw new Error(confirmError.message || t.checkout.paymentError);
          }

          if (paymentIntent?.status === 'succeeded') {
            paymentAPI
              .createContractAfterPayment({
                paymentIntentId: paymentIntent.id,
                itemType: checkoutItem.type,
                itemId: checkoutItem.id,
              })
              .catch((err) => console.error('Failed to create contract:', err));
            await waitForOrderCompletion('subscription');
            const subscriptionSearchParams = new URLSearchParams({
              payment_intent: paymentIntent.id,
              itemType: checkoutItem.type,
              itemId: checkoutItem.id,
            });
            const coupon =
              appliedGiftCard?.code ||
              (promotion?.title
                ? typeof promotion.title === 'string'
                  ? promotion.title
                  : promotion.title.en || Object.values(promotion.title)[0]
                : undefined);
            navigate(`/purchase-confirmation?${subscriptionSearchParams.toString()}`, {
              state: {
                name: checkoutItem.name,
                type: checkoutItem.type,
                amount: finalAmount,
                giftCardUsed: giftCardBalance > 0,
                ...(coupon && { coupon }),
                customerEmail: userProfile?.email || user?.email || '',
                customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
              },
            });
          } else if (paymentIntent?.status === 'requires_payment_method') {
            throw new Error('Payment failed. Please try a different card.');
          }
        } else if (confirmData.success) {
          await waitForOrderCompletion('subscription');
          const subscriptionSuccessParams = new URLSearchParams({
            itemType: checkoutItem.type,
            itemId: checkoutItem.id,
          });
          const coupon =
            appliedGiftCard?.code ||
            (promotion?.title
              ? typeof promotion.title === 'string'
                ? promotion.title
                : promotion.title.en || Object.values(promotion.title)[0]
              : undefined);
          navigate(`/purchase-confirmation?${subscriptionSuccessParams.toString()}`, {
            state: {
              name: checkoutItem.name,
              type: checkoutItem.type,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        } else {
          throw new Error(confirmData.error || confirmData.details || t.checkout.paymentError);
        }
      } else if (
        intentType === 'setup_intent' ||
        intentType === 'setup_intent_for_future' ||
        intentType === 'no_payment_required'
      ) {
        // Handle setup intent (for $0 invoices or trials) or no payment required
        if (intentType === 'no_payment_required' && !clientSecret) {
          await waitForOrderCompletion(checkoutItem.type);
          const noPaymentParams = new URLSearchParams({
            itemType: checkoutItem.type,
            itemId: checkoutItem.id,
          });
          const coupon =
            appliedGiftCard?.code ||
            (promotion?.title
              ? typeof promotion.title === 'string'
                ? promotion.title
                : promotion.title.en || Object.values(promotion.title)[0]
              : undefined);
          navigate(`/purchase-confirmation?${noPaymentParams.toString()}`, {
            state: {
              name: checkoutItem.name,
              type: checkoutItem.type,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
          return;
        }

        // Confirm setup intent for future payments
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: card,
            billing_details: {
              name: name,
              email: email,
            },
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message || t.checkout.paymentError);
        }

        if (setupIntent?.status === 'succeeded') {
          await waitForOrderCompletion(checkoutItem.type);
          const setupIntentParams = new URLSearchParams({
            itemType: checkoutItem.type,
            itemId: checkoutItem.id,
          });
          const coupon =
            appliedGiftCard?.code ||
            (promotion?.title
              ? typeof promotion.title === 'string'
                ? promotion.title
                : promotion.title.en || Object.values(promotion.title)[0]
              : undefined);
          navigate(`/purchase-confirmation?${setupIntentParams.toString()}`, {
            state: {
              name: checkoutItem.name,
              type: checkoutItem.type,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        }
      } else if (clientSecret) {
        // Handle payment intent (for regular payments)
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: {
              card: card,
              billing_details: {
                name: name,
                email: email,
              },
            },
          }
        );

        if (stripeError) {
          throw new Error(stripeError.message || t.checkout.paymentError);
        }

        if (paymentIntent?.status === 'succeeded') {
          paymentAPI
            .createContractAfterPayment({
              paymentIntentId: paymentIntent.id,
              itemType: checkoutItem.type,
              itemId: checkoutItem.id,
            })
            .catch((err) => console.error('Failed to create contract:', err));
          await waitForOrderCompletion(checkoutItem.type);
          const searchParams = new URLSearchParams({
            payment_intent: paymentIntent.id,
            itemType: checkoutItem.type,
            itemId: checkoutItem.id,
          });
          const coupon =
            appliedGiftCard?.code ||
            (promotion?.title
              ? typeof promotion.title === 'string'
                ? promotion.title
                : promotion.title.en || Object.values(promotion.title)[0]
              : undefined);
          navigate(`/purchase-confirmation?${searchParams.toString()}`, {
            state: {
              name: checkoutItem.name,
              type: checkoutItem.type,
              amount: finalAmount,
              giftCardUsed: giftCardBalance > 0,
              ...(coupon && { coupon }),
              customerEmail: userProfile?.email || user?.email || '',
              customerPhone: userProfile?.phoneNumber || user?.phone_number || '',
            },
          });
        }
      } else {
        throw new Error('Invalid payment response from server');
      }
    } catch (err: any) {
      setError(getErrorMessage(err, t, t.checkout.paymentError));
      setIsProcessing(false);
    }
  };

  return (
    <>
      {isPollingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 max-w-sm rounded-lg bg-white p-8 text-center shadow-xl">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-green-600"></div>
            <h3 className="mb-2 text-xl font-bold text-gray-800">
              {t.checkout.processingOrder || 'Processing Order'}
            </h3>
            <p className="text-gray-600">
              {t.checkout.pleaseWait || 'Please wait while we confirm your purchase...'}
            </p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-4 space-y-2">
          <p className="mb-2 text-sm text-gray-600">{t.checkout.customerInfo}:</p>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              {t.checkout.name} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 block w-full rounded-md border p-2 shadow-sm focus:border-green-500 focus:ring-green-500 ${
                attemptedSubmit && name.trim() === ''
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300'
              }`}
              required
            />
            {attemptedSubmit && name.trim() === '' && (
              <p className="mt-1 text-sm text-red-600">
                {t.booking.nameRequired || 'Name is required'}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              {t.checkout.email} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 block w-full rounded-md border p-2 shadow-sm focus:border-green-500 focus:ring-green-500 ${
                attemptedSubmit && email.trim() === ''
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300'
              }`}
              required
            />
            {attemptedSubmit && email.trim() === '' && (
              <p className="mt-1 text-sm text-red-600">
                {t.booking.emailRequired || 'Email is required'}
              </p>
            )}
          </div>
        </div>

        {finalAmount > 0 || (finalAmount === 0 && checkoutItem.type === 'subscription') ? (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block font-medium text-gray-700">
                {t.checkout.cardNumber || 'Card Number'}
              </label>
              <div className="rounded-md border border-gray-300 p-4">
                <CardNumberElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#32325d',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block font-medium text-gray-700">
                  {t.checkout.expiryDate || 'Expiry Date'}
                </label>
                <div className="rounded-md border border-gray-300 p-4">
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#32325d',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block font-medium text-gray-700">
                  {t.checkout.cvc || 'CVC'}
                </label>
                <div className="rounded-md border border-gray-300 p-4">
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#32325d',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded border border-green-300 bg-green-50 px-4 py-3 text-green-700">
            <p>
              {checkoutItem.type === 'subscription'
                ? 'Your first payment is fully covered by the gift card! We still need your payment details for future charges.'
                : 'Your purchase is fully covered by the gift card!'}
            </p>
          </div>
        )}

        {error && (
          <div className="rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          onClick={(e) => {
            if (!stripe || isProcessing || name.trim() === '' || email.trim() === '') {
              e.preventDefault();
              setAttemptedSubmit(true);
            }
          }}
          className={`w-full rounded-md px-4 py-3 font-medium transition-colors ${
            !stripe || isProcessing || name.trim() === '' || email.trim() === ''
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {isProcessing
            ? t.checkout.processing
            : finalAmount === 0
              ? t.checkout.completeOrder
              : t.checkout.payAmount.replace('{{amount}}', formatPrice(finalAmount))}
        </button>
      </form>
    </>
  );
};

export default CheckoutForm;
